import React, { useState } from "react";
import {
  Button,
  IconButton,
  MenuItem,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { useAlert } from "contexts/alertContext";
import DeleteIcon from "@mui/icons-material/Delete";
import { ICompanyJob } from "models/companyJob";
import { IAcquireSources } from "models/employeeCompetencies";
import { ICompetencyByRole } from "models/competency";

type Entry = {
  competencyId: string;
  scoreId: number;
};

interface Props {
  competencies: ICompetencyByRole[];
  scores: IAcquireSources[];
  entries: Entry[];
  setEntries: (entries: Entry[]) => void;
}

function isCompetencyScoreCombinationFound(
  data: ICompanyJob[],
  selectedCompetencyId: string,
  selectedScoreId: string
) {
  for (const item of data) {
    if (
      item.companyDepartmentId === selectedCompetencyId &&
      item.jobId === selectedScoreId
    ) {
      return true;
    }
  }

  return false;
}

const EditCompetencyScore: React.FC<Props> = ({
  competencies,
  scores,
  entries,
  setEntries,
}) => {
  const { t } = useTranslation();

  const [score, setScore] = useState<number>(0);
  const [competency, setCompetency] = useState<string>("");

  const { setAlert } = useAlert();

  const competencyOptions = competencies.map((item) => ({
    value: item.id,
    label: item.name,
  }));

  const scoreOptions = scores.map((item) => ({
    value: item.id,
    label: item.name,
  }));

  const handleAddEntry = () => {
    if (competency) {
      const competencyExists = entries.filter(
        (entry) => entry.competencyId === competency
      );
      const isCompination = competencyExists.some(
        (entry) => entry.scoreId === score
      );
      if (!isCompination) {
        const newEntry: Entry = {
          competencyId: competency,
          scoreId: score!,
        };
        setEntries([...entries, newEntry]);
        setCompetency("");
        setScore(0);
      } else {
        setAlert({
          open: true,
          message: "Already Exists",
          type: "info",
        });
      }
    }
  };

  const handleDeleteEntry = (index: number) => {
    const newEntries = entries.filter((_, i) => i !== index);
    setEntries(newEntries);
  };

  return (
    <Stack spacing={2} p={2}>
      <div>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            gap: "10px",
          }}
        >
          <Select
            value={competency}
            onChange={(e) => setCompetency(e.target.value as string)}
            displayEmpty
          >
            <MenuItem value="" disabled>
              {t("T_competency")}
            </MenuItem>
            {competencyOptions.map((dep) => (
              <MenuItem key={dep.value} value={dep.value}>
                {dep.label}
              </MenuItem>
            ))}
          </Select>

          <Select
            value={score}
            onChange={(e) => setScore(e.target.value as number)}
          >
            <MenuItem value={0} disabled>
              {t("T_acquire_source")}
            </MenuItem>
            {scoreOptions.map((job) => (
              <MenuItem key={job.value} value={job.value}>
                {job.label}
              </MenuItem>
            ))}
          </Select>

          <Button
            variant="contained"
            style={{ borderRadius: 0 }}
            onClick={handleAddEntry}
            disabled={!competency || !score}
          >
            {t("T_add")}
          </Button>
        </div>

        <Table>
          <TableHead>
            <TableRow>
              <TableCell>{t("T_competency")}</TableCell>
              <TableCell>{t("T_acquire_source")}</TableCell>

              <TableCell>{t("T_action")}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {entries.map((entry, index) => (
              <TableRow key={index}>
                <TableCell>
                  {
                    competencies.find((job) => job.id === entry.competencyId)
                      ?.name
                  }
                </TableCell>
                <TableCell>
                  {scores.find((job) => job.id === entry.scoreId)?.name}
                </TableCell>
                <TableCell>
                  <IconButton onClick={() => handleDeleteEntry(index)}>
                    <DeleteIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </Stack>
  );
};

export default EditCompetencyScore;
